import { orderBy } from 'lodash';
import i18next from 'i18next';
import React from 'react';

import { DATE_PICKER_DOT_COLOR } from '../../../commons/DatePickers/constants';
import { formatNumber } from '../../../commons/DisplayNumber';
import { Label } from '../../../commons/utils/styledLibraryComponents';

import theme from '../../../theme';

export function getGraphConfiguration(entity, selectedMetric) {
  return {
    title: i18next.t('STOCKS.PAST_STOCKS.TITLE', {
      name: entity.name,
      status: entity.active ? i18next.t('GENERAL.ACTIVE') : i18next.t('GENERAL.INACTIVE'),
    }),
    tooltiptext: i18next.t('STOCKS.PAST_STOCKS.TOOLTIP'),
    metric: selectedMetric,
    legends: [
      {
        color: theme.colors.brand.secondary,
        name: i18next.t('STOCKS.STOCKS.THEORETICAL_STOCK'),
        propertyKey:
          selectedMetric === 'turnover' ? 'theoricalStockTurnover' : 'theoricalStockUnit',
      },
      {
        color: theme.colors.greys.light,
        name: i18next.t('STOCKS.STOCKS.REAL_STOCK'),
        propertyKey: selectedMetric === 'turnover' ? 'realStockTurnover' : 'realStockUnit',
      },
    ],
  };
}

export const getEntityUnit = (unit, condensed = true, count = 1) => {
  if (unit === 'g' || unit === 'kg' || unit === 'Kg') {
    return 'kg';
  } else if (unit === 'mL' || unit === 'L') {
    return 'L';
  } else {
    return condensed ? 'u.' : i18next.t('GENERAL.UNIT', { count });
  }
};

export const getCalendarInfo = (storeName) => ({
  dotsInformations: [
    {
      dotColor: DATE_PICKER_DOT_COLOR.GREEN,
      dotInfo: i18next.t('STOCKS.PAST_STOCKS.DATEPICKER_TOOLTIP', {
        storeName: storeName.toLowerCase(),
      }),
    },
  ],
});

export const sortByGapPercentage = (data, orderType, customSortProperty) => {
  const notNullValues = data.filter((stock) => stock[customSortProperty] != null);
  const nullValues = data.filter((stock) => stock[customSortProperty] == null);
  const absoluteSortedNotNullValues = orderBy(
    notNullValues,
    [(item) => Math.abs(item[customSortProperty])],
    [orderType],
  );
  const alphaSortNullValues = orderBy(nullValues, (item) => item.name.toLowerCase(), ['asc']);

  return absoluteSortedNotNullValues.concat(alphaSortNullValues);
};

export const renderGapPercentage = (value, gapValueLimit) => {
  if (value == null) {
    return '--';
  }

  const formattedValue = value.toFixed(2);

  let gapValue = `${formattedValue} %`;

  if (Math.abs(value) >= gapValueLimit) {
    return (
      <Label
        background={theme.colors.infoLightOrange}
        children={gapValue}
        color={theme.colors.black}
        type={'plain'}
        useNestedListRowFont
      />
    );
  }

  return gapValue;
};

const renderStockGap = (value, item, metricKey, currency, gapValueLimit) => {
  if (value == null) {
    return '--';
  }

  const formattedValue = value.toFixed(2);

  const stockGapValue =
    metricKey === 'unit'
      ? `${formattedValue} ${getEntityUnit(item.unit)}`
      : `${formattedValue} ${currency.alphabeticCode}`;

  if (Math.abs(item.stockGapPercentage) >= gapValueLimit) {
    return (
      <Label
        background={theme.colors.infoLightOrange}
        children={stockGapValue}
        color={theme.colors.black}
        type={'plain'}
        useNestedListRowFont
      />
    );
  }

  return stockGapValue;
};

const getStockValueByMetric = (
  value,
  item,
  metricKey,
  currency,
  allowNegativeValueDisplay = false,
) => {
  const displayValue = allowNegativeValueDisplay || value >= 0 ? value : 0;

  if (metricKey === 'unit') {
    return `${displayValue.toFixed(2)} ${getEntityUnit(item.unit)}`;
  }

  return `${formatNumber(displayValue, currency.numberDecimals)} ${currency.alphabeticCode}`;
};

export const computeTheoreticalTotal = (displayedPastStocks) => {
  const total = displayedPastStocks.reduce((result, { theoricalStock }) => {
    const value = theoricalStock.turnover;

    if (value == null) {
      return result;
    }

    return result + Math.round(value * 100) / 100;
  }, null);

  if (total === null) {
    return null;
  }

  return Math.round(total * 100) / 100;
};

export const computeRealTotal = (displayedPastStocks) => {
  const total = displayedPastStocks.reduce((result, { realStockWithRecipes }) => {
    const value = realStockWithRecipes.turnover;

    if (value == null) {
      return result;
    }

    return result + Math.max(Math.round(value * 100) / 100, 0);
  }, null);

  if (total === null) {
    return null;
  }

  return Math.round(total * 100) / 100;
};

export const computeCentralKitchenPastRecipesTheoreticalTotal = (displayedPastStocks) => {
  const total = displayedPastStocks.reduce((result, { theoricalStock }) => {
    const value = theoricalStock.turnover;

    if (value == null) {
      return result;
    }

    return result + Math.round(value * 100) / 100;
  }, null);

  if (total === null) {
    return null;
  }

  return Math.round(total * 100) / 100;
};

export const computeCentralKitchenPastRecipesRealTotal = (displayedPastStocks) => {
  const total = displayedPastStocks.reduce((result, { realStock }) => {
    const value = realStock.turnover;

    if (value == null) {
      return result;
    }

    return result + Math.max(Math.round(value * 100) / 100, 0);
  }, null);

  if (total === null) {
    return null;
  }

  return Math.round(total * 100) / 100;
};

export default {
  getGraphConfiguration,
  getEntityUnit,
  sortByGapPercentage,
  renderGapPercentage,
  renderStockGap,
  getStockValueByMetric,
};
